// Renders the in-app notification banner stack (top-right, slide-in,
// auto-dismiss, tap-to-open). Cross-platform: React Native primitives +
// NativeWind classes so it works on web (react-native-web) and the desktop
// RN-macOS app unchanged. Mount once near the root of each app's shell.
//
// On web the stack is portaled to `document.body` (via `WebPortal`) with the
// maximum z-index so it sits ABOVE every other surface — modals, overlays,
// dropdowns — regardless of their own stacking contexts. On native the portal
// is a passthrough and the View renders in-tree.

import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Animated,
  PanResponder,
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from 'react-native';

import { WebPortal } from '../chat/composer/WebPortal';
import { PingLogomark } from '../icons/PingLogomark';
import { type BannerItem, useBannerStore } from './bannerStore';

/** How long a banner stays up before auto-dismissing. */
const BANNER_TTL_MS = 5_000;

const IS_WEB = Platform.OS === 'web';

export interface NotificationBannerHostProps {
  /** Open the conversation a tapped banner refers to. */
  readonly onOpenConversation?: (conversationId: string) => void;
}

// Premium drop shadow (react-native-web converts shadow* → boxShadow).
const CARD_SHADOW = {
  shadowColor: '#000000',
  shadowOpacity: 0.2,
  shadowRadius: 2,
  shadowOffset: { width: 0, height: 2 },
  elevation: 2,
} as const;

function NotificationBanner({
  item,
  onOpen,
}: {
  item: BannerItem;
  onOpen: (item: BannerItem) => void;
}): JSX.Element {
  const dismiss = useBannerStore((s) => s.dismiss);
  const translateY = useRef(new Animated.Value(-16)).current;
  const translateX = useRef(new Animated.Value(0)).current;
  const opacity = useRef(new Animated.Value(0)).current;
  const scale = useRef(new Animated.Value(0.96)).current;

  const animateOut = useCallback(
    (after: () => void) => {
      Animated.parallel([
        Animated.timing(translateY, {
          toValue: -16,
          duration: 150,
          useNativeDriver: !IS_WEB,
        }),
        Animated.timing(opacity, { toValue: 0, duration: 150, useNativeDriver: !IS_WEB }),
        Animated.timing(scale, { toValue: 0.96, duration: 150, useNativeDriver: !IS_WEB }),
      ]).start(after);
    },
    [opacity, scale, translateY],
  );

  // Swipe-right to dismiss (mouse-drag on web, touch on native). Follows the
  // pointer; past ~96px it flings off-screen and dismisses, otherwise snaps
  // back. Only claims horizontal drags so taps + vertical scroll pass through.
  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onMoveShouldSetPanResponder: (_e, g) =>
          Math.abs(g.dx) > 8 && Math.abs(g.dx) > Math.abs(g.dy),
        onPanResponderMove: (_e, g) => {
          translateX.setValue(Math.max(0, g.dx));
        },
        onPanResponderRelease: (_e, g) => {
          if (g.dx > 96 || g.vx > 0.5) {
            Animated.parallel([
              Animated.timing(translateX, { toValue: 420, duration: 160, useNativeDriver: !IS_WEB }),
              Animated.timing(opacity, { toValue: 0, duration: 160, useNativeDriver: !IS_WEB }),
            ]).start(() => dismiss(item.id));
          } else {
            Animated.spring(translateX, {
              toValue: 0,
              tension: 120,
              friction: 14,
              useNativeDriver: !IS_WEB,
            }).start();
          }
        },
      }),
    // item.id / dismiss are stable for a given banner.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    Animated.parallel([
      Animated.spring(translateY, {
        toValue: 0,
        tension: 90,
        friction: 13,
        useNativeDriver: !IS_WEB,
      }),
      Animated.spring(scale, { toValue: 1, tension: 90, friction: 13, useNativeDriver: !IS_WEB }),
      Animated.timing(opacity, { toValue: 1, duration: 200, useNativeDriver: !IS_WEB }),
    ]).start();

    const timer = setTimeout(() => animateOut(() => dismiss(item.id)), BANNER_TTL_MS);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Animated.View
      {...panResponder.panHandlers}
      // Animation + stack spacing ONLY. NativeWind's className is NOT applied to
      // Animated.View — css-interop doesn't wrap `Animated.createAnimatedComponent`
      // components, so any bg/border/radius class set here silently no-ops on web
      // (the class string is dropped instead of compiled to inline style). The
      // visually-styled card is the real <View> below, which IS interop'd.
      style={{
        transform: [{ translateY }, { translateX }, { scale }],
        opacity,
        marginBottom: 10,
      }}
    >
      <View
        // Shadow stays inline (it must live on the same box as the radius so the
        // cast follows the rounded corners). Everything else is design-token
        // classes, theme-aware via `dark:`. A mention swaps the subtle border for
        // the brand accent + a left bar.
        style={CARD_SHADOW}
        className={`min-w-[280px] max-w-[92vw] overflow-hidden rounded-md border bg-light-surface-panel dark:bg-dark-surface-panel ${
          item.isMention
            ? 'border-light-accent-brand/55 dark:border-dark-accent-brand/55'
            : 'border-light-border-subtle dark:border-dark-border-subtle'
        }`}
      >
        <Pressable
          onPress={() => onOpen(item)}
          accessibilityRole="button"
          accessibilityLiveRegion="polite"
          className="flex flex-row items-stretch"
        >
          {/* Mention accent bar (full height, clipped by the card radius). */}
          {item.isMention ? (
            <View className="w-[3px] bg-light-accent-brand dark:bg-dark-accent-brand" />
          ) : null}

          <View className="flex flex-1 flex-row items-center gap-6 p-2">
            {/* Ping logomark. */}
            <View>
              <PingLogomark size={40} accessibilityLabel="Ping" />
            </View>

            {/* Title + body */}
            <View className="min-w-0 flex-1">
              <View className="flex flex-row items-center gap-1.5">
                <Text
                  numberOfLines={1}
                  className="flex-1 text-[14.5px] font-bold leading-[19px] text-light-text-primary dark:text-dark-text-primary"
                >
                  {item.title}
                </Text>
                {item.isMention ? (
                  <View className="rounded-full bg-light-accent-brand/15 dark:bg-dark-accent-brand/15 px-1.5 py-0.5">
                    <Text className="text-[10px] font-bold text-light-accent-brand dark:text-dark-accent-brand">
                      @you
                    </Text>
                  </View>
                ) : null}
              </View>
              <Text
                numberOfLines={2}
                className="mt-1 text-[13px] leading-[18px] text-light-text-secondary dark:text-dark-text-secondary"
              >
                {item.body}
              </Text>
            </View>

            {/* Dismiss */}
            <Pressable
              onPress={() => animateOut(() => dismiss(item.id))}
              hitSlop={10}
              accessibilityRole="button"
              accessibilityLabel="Dismiss notification"
              className="-mr-1 -mt-1 h-7 w-7 items-center justify-center self-start rounded-full active:bg-light-surface-elevated dark:active:bg-dark-surface-elevated"
            >
              <Text className="text-[17px] leading-[17px] text-light-text-tertiary dark:text-dark-text-tertiary">
                ×
              </Text>
            </Pressable>
          </View>
        </Pressable>
      </View>
    </Animated.View>
  );
}

export function NotificationBannerHost({
  onOpenConversation,
}: NotificationBannerHostProps): JSX.Element | null {
  const banners = useBannerStore((s) => s.banners);
  const dismiss = useBannerStore((s) => s.dismiss);

  const handleOpen = useCallback(
    (item: BannerItem) => {
      dismiss(item.id);
      if (item.conversationId && onOpenConversation) {
        onOpenConversation(item.conversationId);
      }
    },
    [dismiss, onOpenConversation],
  );

  if (banners.length === 0) return null;

  // Position via an inline style rather than NativeWind classes: on web the
  // host is portaled to <body>, and `position: fixed` + a max z-index are
  // applied directly so the stack reliably sits TOP-RIGHT above every other
  // surface (modals, overlays) regardless of css-interop quirks. On native the
  // portal is a passthrough and `position: absolute` anchors it in-tree.
  // `position: 'fixed'` is valid on react-native-web but not in RN's ViewStyle
  // type, hence the cast.
  const overlayStyle = (
    IS_WEB
      ? { position: 'fixed', top: 16, right: 16, zIndex: 2147483600, alignItems: 'flex-end' }
      : { position: 'absolute', top: 16, right: 16, zIndex: 10000, alignItems: 'flex-end' }
  ) as unknown as ViewStyle;

  return (
    <WebPortal>
      {/* Passthrough overlay — only the banners themselves are interactive. */}
      <View pointerEvents="box-none" style={overlayStyle}>
        {banners.map((item) => (
          <NotificationBanner key={item.id} item={item} onOpen={handleOpen} />
        ))}
      </View>
    </WebPortal>
  );
}
